import { Receipt, Wallet, Landmark } from "lucide-react";
import { SectionCard, KpiCard, Segmented, InfoNote, Row } from "@/components/common/Cards";
import { Field, MoneyInput } from "@/components/common/Inputs";
import { Sel } from "@/components/common/Sel";
import { PPH42_TYPES } from "@/lib/engines/tax";
import { fmtRp, fmtPct } from "@/lib/format";
import { PpnControls } from "@/components/tax/Pph23Panel";

const groups = Object.values(
  PPH42_TYPES.reduce((a, t) => {
    a[t.group] = a[t.group] || { label: t.group, options: [] };
    a[t.group].options.push({ value: t.key, label: `${t.label} — ${String(t.rate).replace(".", ",")}%` });
    return a;
  }, {})
);

export default function Pph42Panel({ data, setData, result: r, readOnly: ro }) {
  const f = (k) => (v) => setData((d) => ({ ...d, [k]: v }));
  const isUmkm = data.type === "umkm";
  return (
    <div className="grid xl:grid-cols-[420px_1fr] gap-5">
      <SectionCard title="Input Transaksi" eyebrow="PPh Final Pasal 4 ayat (2)" testid="pph42-input-card">
        <div className="space-y-4">
          <Field label="Jenis penghasilan">
            <Sel value={data.type} onChange={f("type")} groups={groups} testid="pph42-type-select" disabled={ro} />
          </Field>
          {!isUmkm && (
            <Field label="Dasar nilai">
              <Segmented value={data.basis} onChange={f("basis")} testid="pph42-basis" size="sm" disabled={ro} options={[{ value: "gross", label: "Gross (bruto)" }, { value: "net", label: "Net diterima (gross-up)" }]} />
            </Field>
          )}
          <Field label={isUmkm ? "Omzet bruto periode ini" : data.basis === "net" ? "Nilai bersih yang ingin diterima" : "Nilai bruto transaksi"}>
            <MoneyInput value={data.amount} onChange={f("amount")} testid="pph42-amount-input" disabled={ro} />
          </Field>
          {isUmkm && (
            <>
              <Field label="Jenis wajib pajak">
                <Segmented value={data.umkm.wpType} onChange={(v) => setData((d) => ({ ...d, umkm: { ...d.umkm, wpType: v } }))} testid="pph42-umkm-wp" size="sm" disabled={ro} options={[{ value: "op", label: "Orang pribadi" }, { value: "badan", label: "Badan (PT/CV/Koperasi)" }]} />
              </Field>
              {data.umkm.wpType === "op" && (
                <Field label="Omzet kumulatif sebelumnya (tahun berjalan)" hint="Untuk menghitung batas omzet tidak kena pajak Rp500 juta.">
                  <MoneyInput value={data.umkm.omzetKumulatif} onChange={(v) => setData((d) => ({ ...d, umkm: { ...d.umkm, omzetKumulatif: v } }))} testid="pph42-umkm-kumulatif-input" disabled={ro} />
                </Field>
              )}
            </>
          )}
          {r.type.ppn && <PpnControls ppn={data.ppn} onChange={f("ppn")} ro={ro} testid="pph42" />}
        </div>
      </SectionCard>
      <div className="space-y-5">
        <div className="grid sm:grid-cols-3 gap-3">
          <KpiCard label="PPh final" value={fmtRp(r.pph)} sub={`Tarif ${fmtPct(r.rate)}`} accent icon={Landmark} testid="pph42-kpi-tax" />
          <KpiCard label="Nilai bruto" value={fmtRp(r.bruto)} sub={r.ppn ? `+ PPN ${fmtRp(r.ppn)}` : "Tanpa PPN"} icon={Receipt} testid="pph42-kpi-bruto" />
          <KpiCard label={isUmkm ? "Omzet setelah pajak" : "Diterima bersih"} value={fmtRp(r.netRecipient)} icon={Wallet} testid="pph42-kpi-net" />
        </div>
        <SectionCard title="Rincian" description={r.type.label} testid="pph42-breakdown">
          <Row label="Nilai bruto" value={fmtRp(r.bruto)} />
          {isUmkm && r.taxable !== r.bruto && <Row label="Omzet yang dikenai pajak" value={fmtRp(r.taxable)} muted />}
          {r.ppn > 0 && <Row label={`PPN ${fmtPct(r.ppnRate)}`} value={fmtRp(r.ppn)} />}
          {r.ppn > 0 && <Row label="Total tagihan" value={fmtRp(r.invoice)} strong />}
          <Row label={`PPh final ${fmtPct(r.rate)}`} value={fmtRp(-r.pph)} negative />
          {!isUmkm && <Row label="Dibayarkan ke penerima" value={fmtRp(r.paidToRecipient)} strong testid="pph42-paid-row" />}
          <Row label="Penghasilan bersih penerima" value={fmtRp(r.netRecipient)} muted />
          {r.umkmNote && <InfoNote className="mt-4">{r.umkmNote}</InfoNote>}
          <InfoNote className="mt-4">
            PPh 4 ayat (2) bersifat <b>final</b> — tidak dapat dikreditkan. Untuk sewa & jasa konstruksi, pajak dipotong oleh penyewa/pengguna jasa; UMKM disetor sendiri oleh wajib pajak paling lambat tanggal 15 bulan berikutnya.
          </InfoNote>
        </SectionCard>
      </div>
    </div>
  );
}
